import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {Product} from "../../../model/Product";
import { DatePipe } from '@angular/common';
import {ProductService} from "../../service-data/product.service";
import { DecimalPipe } from '@angular/common';
import { LANGUAGES, LanguageEntry } from "../../../model/Languages";
import { LanguagePipe } from "../../pipe/language.pipe";
import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';

@Component({
  selector: 'app-product',
  templateUrl: './product.component.html',
  styleUrls: ['./product.component.scss']
})

export class ProductComponent implements OnInit { // <-- UPDATED CLASS NAME
  @Input() product1!: Product;
  @Input() product2!: Product;
  @Input() heading1: String;
  @Input() heading2: String;
  @Input() isEditingAnOption: boolean = false; // Determines if the edit button is shown

  // @Input() displayCover: boolean;
  // @Input() editingDetails: string;
  // @Output() editingDetailsChange = new EventEmitter<any>();

  @Output() save = new EventEmitter<Product>();
  @Output() cancel = new EventEmitter<void>();

  productForm!: FormGroup;
  isEditMode: boolean = false; // Toggles between display and form view

  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],              // custom button values
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],      // superscript/subscript
      [{ 'indent': '-1'}, { 'indent': '+1' }],          // outdent/indent
      [{ 'direction': 'rtl' }],                         // text direction
      [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['clean'],                                         // remove formatting button
      ['link']                                           // link button
    ]
  };

  // Central language list — see src/model/Languages.ts.
  // Entries with ISO 639-1 (2-letter) codes come first (sorted by
  // that code); the rest are sorted by ISO 639-3 (3-letter) code.
  // When an entry has `translatable: true` the display name is
  // routed through LanguagePipe.get(); otherwise the raw English
  // `name` is used as the display label.
  languages: LanguageEntry[] = LANGUAGES;
  filteredLanguages$: Observable<LanguageEntry[]>;

  /** Locale-aware label for a language entry in the combo box. */
  languageDisplayName(entry: LanguageEntry): string {
    if (!entry) { return ''; }
    return entry.translatable ? LanguagePipe.get(entry.name, 'compressSpaces') : entry.name;
  }

  /** Value shown in the input for a given selected iso639_3 code. */
  displayLanguageCode(code: string): string {
    if (!code) { return ''; }
    const entry = this.languages.find(l => l.iso639_3 === code || l.iso639_2 === code);
    return entry ? this.languageDisplayName(entry) + ' (' + entry.iso639_3 + ')' : code;
  }

  private _filterLanguages(value: string): LanguageEntry[] {
    const filter = (value || '').toString().trim().toLowerCase();
    if (!filter) { return this.languages; }
    return this.languages.filter(l =>
      l.iso639_3.toLowerCase().indexOf(filter) === 0 ||
      (l.iso639_2 && l.iso639_2.toLowerCase().indexOf(filter) === 0) ||
      this.languageDisplayName(l).toLowerCase().indexOf(filter) !== -1
    );
  }

  // Reference date for comparison (1/1/2000)
  private minDate = new Date('2000-01-01');

  constructor(private fb: FormBuilder, private productService: ProductService, private decimalPipe: DecimalPipe) {
  }

  ngOnInit() {
    this.initForm(this.product2);
    this.formatInitialMSRP();
  }

  /**
   * Initializes the Reactive Form with product data.
   */
  initForm(product: Product) {
    this.productForm = this.fb.group({
      customerProduct_ID: [product?.customerProduct_ID],
      EAN13: [product?.EAN13],
      title: [product?.title, Validators.required],
      subtitle: [product?.subtitle],
      productAuthor: [product?.productAuthor],
      productPublicationYear: [product?.productPublicationYear],
      shortDescription: [product?.shortDescription],
      description: [product?.description],
      languageCode: [product?.languageCode],
      previewPages: [product?.previewPages],
      msrp: [product?.msrp],
      msrpCurrency: [product?.msrpCurrency || 'USD']
    });

    if (!this.isEditMode) {
      this.productForm.disable(); // Disable inputs if not in edit mode initially
    }

    // Wire the language combo box's autocomplete filter to the
    // `languageCode` form control so users can type to filter by
    // ISO 639-1/639-3 code or by (locale-translated) name.
    this.filteredLanguages$ = this.productForm.get('languageCode').valueChanges.pipe(
      startWith(this.productForm.get('languageCode').value || ''),
      map(value => this._filterLanguages(value))
    );
  }

  onEditorCreated(editor: any): void {
    // Check if the form control has a value and if the editor is empty
    const initialHtml = this.productForm.get('description')?.value;

    // Check if we have HTML and if Quill hasn't rendered anything yet
    if (initialHtml && initialHtml.trim().length > 0 && editor.root.innerHTML.trim() === '<p><br></p>') {
      // Use the editor's API to set the content using HTML
      editor.root.innerHTML = initialHtml;
      this.productForm.get('description')?.markAsPristine();
    }
  }

  /**
   * Toggles the component into "edit" mode.
   */
  toggleEditMode() {
    this.isEditMode = true;
    this.productForm.enable();
  }

  /**
   * Handles the Save button click.
   */
  onSave() {
    if (this.productForm.valid) {
      // Merge form values back with read-only properties
      const updatedProduct: Product = {
        ...this.product2,
        ...this.productForm.value,
      };
      this.save.emit(updatedProduct);
      this.isEditMode = false;
      this.productForm.disable();
    }
  }

  /**
   * Handles the Cancel button click.
   */
  onCancel() {
    this.productForm.reset(this.product2); // Reset form controls to initial product values
    this.cancel.emit();
    this.isEditMode = false;
    this.productForm.disable();
  }

  /**
   * Custom date formatter for read-only dates.
   * If date is not newer than 1/1/2000, shows "-".
   */
  formatDate(date: Date | string | null): string {
    if (!date) {
      return '----';
    }

    // Ensure it's a Date object for comparison
    const checkDate = (date instanceof Date) ? date : new Date(date);

    if (checkDate.getTime() <= this.minDate.getTime()) {
      return '----';
    }

    // Use Angular's built-in date formatting (e.g., 'shortDate' or custom format)
    return new DatePipe('en-US').transform(checkDate, 'MM/dd/yyyy') || '----';
  }

  formatMSRP(event: any) {
    const value = event.target.value;

    if (!value || isNaN(parseFloat(value))) {
      // If empty or not a number, set to '0.00' or leave blank based on preference
      this.productForm.get('msrp')?.setValue(this.decimalPipe.transform(0, '1.2-2'));
      return;
    }

    // 1. Get the numeric value
    const numericValue = parseFloat(value);

    // 2. Format using the DecimalPipe: '1.2-2' means min 1 integer, min 2 and max 2 fraction digits.
    const formattedValue = this.decimalPipe.transform(numericValue, '1.2-2');

    // 3. Update the form control value
    this.productForm.get('msrp')?.setValue(formattedValue);
  }
  formatInitialMSRP() {
    const msrpControl = this.productForm.get('msrp');

    if (msrpControl && msrpControl.value) {
      const rawValue = parseFloat(msrpControl.value);
      const formattedValue = this.decimalPipe.transform(rawValue, '1.2-2');
      msrpControl.setValue(formattedValue, { emitEvent: false });
    }
  }
}


